import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { getPCForWarehouseList } from 'src/utils/Queries';
import { GetPCForWarehouseListResponse } from 'src/features/types/gslaApi';
import { useIsDefaultFCPCEnabled } from './useIsDefaultFCPCEnabled';

/**
 * React Query hook for fetching production capabilities for multiple warehouses
 * with automatic Default PC appending and caching.
 * 
 * Default_FC_PC is only appended when the AFTGranularSLAModelService feature flag
 * has not disabled it. The flag state is determined via the getFCMetadata API.
 */
export const usePCForWarehouse = (
    warehouseIds: string[],
    enabled: boolean = true
) => {
    // Sort warehouse IDs for consistent cache keys
    const sortedWarehouseIds = useMemo(() =>
        [...warehouseIds].sort(),
        [warehouseIds]
    );

    // Check if Default_FC_PC is enabled via the backend feature flag
    const { isDefaultFCPCEnabled } = useIsDefaultFCPCEnabled(sortedWarehouseIds[0]);

    // React Query hook with 10-minute cache duration
    const queryResult = useQuery<GetPCForWarehouseListResponse, Error>(
        ['pc-for-warehouse', ...sortedWarehouseIds],
        async () => {
            const result = getPCForWarehouseList(sortedWarehouseIds);
            if (!result) {
                throw new Error('Failed to fetch production capabilities');
            }
            return result;
        },
        {
            enabled: enabled && sortedWarehouseIds.length > 0,
            staleTime: 10 * 60 * 1000, // 10 minutes
            cacheTime: 15 * 60 * 1000, // 15 minutes
            retry: 2,
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
            // Don't refetch on window focus to avoid unnecessary API calls
            refetchOnWindowFocus: false,
        }
    );

    // Process the API response and append default PCs
    const availablePCs = useMemo(() => {
        if (!queryResult.data) return [];

        const allPCs = new Set<string>();
        
        // Add PCs from API response
        Object.values(queryResult.data.productionCapabilitiesByFC).forEach((pcs: string[]) => {
            pcs.forEach((pc: string) => allPCs.add(pc));
        });
        
        // Only append Default_FC_PC if the backend feature flag has not disabled it
        if (isDefaultFCPCEnabled) {
            allPCs.add('Default_FC_PC');
        }
        allPCs.add('Default_Network_PC');
        
        return Array.from(allPCs).sort();
    }, [queryResult.data, isDefaultFCPCEnabled]);

    return {
        ...queryResult,
        availablePCs,
        isLoading: queryResult.isLoading,
        error: queryResult.error,
        isError: queryResult.isError,
        isSuccess: queryResult.isSuccess,
        refetch: queryResult.refetch,
    };
};

/**
 * Simplified hook that returns just the production capabilities array
 * for easier integration with existing components
 */
export const useProductionCapabilities = (
    warehouseIds: string[],
    enabled: boolean = true
): string[] => {
    const { availablePCs } = usePCForWarehouse(warehouseIds, enabled);
    return availablePCs;
};